import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, ArrowRight } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import { LOGO_URL } from "../../lib/api";

const GEO = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cg fill='none' stroke='%23C99A2E' stroke-width='1'%3E%3Cpath d='M70 8 84 56 132 70 84 84 70 132 56 84 8 70 56 56Z'/%3E%3Ccircle cx='70' cy='70' r='34'/%3E%3C/g%3E%3C/svg%3E")`;
const SPLASH_IMG = "https://static.prod-images.emergentagent.com/jobs/7e10f933-be32-4f7a-b273-ca5f06f1a34e/images/3e8fcdc28d0e115528f117e92c1d369cfa905c922d86b208b6352156003ab2aa.jpeg";

export default function SpiritualIntro() {
  const { settings } = useSettings();
  const [show, setShow] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [muted, setMuted] = useState(true);
  const audioRef = useRef(null);
  const audioUrl = settings?.intro_audio_url || "";

  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith("/admin")) return;
    if (sessionStorage.getItem("hh_intro_seen")) return;
    if (path === "/") {
      setShow(true); // only show the entry splash on the homepage
    } else {
      // Deep link (e.g. from an ad): skip the splash entirely for this session
      sessionStorage.setItem("hh_intro_seen", "1");
    }
  }, []);

  const finish = () => { sessionStorage.setItem("hh_intro_seen", "1"); setShow(false); };
  const enter = (withSound) => {
    if (withSound && audioRef.current && audioUrl) {
      audioRef.current.muted = false;
      audioRef.current.play().then(() => setMuted(false)).catch(() => {});
    }
    setLeaving(true);
    setTimeout(finish, 950);
  };

  const toggleSound = () => {
    if (!audioRef.current || !audioUrl) return;
    if (muted) { audioRef.current.muted = false; audioRef.current.play().catch(() => {}); setMuted(false); }
    else { audioRef.current.muted = true; setMuted(true); }
  };

  return (
    <>
      {audioUrl && <audio ref={audioRef} src={audioUrl} loop muted preload="auto" />}

      {show && (
        <div
          className={`fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overflow-x-hidden px-5 py-10 splash-bg ${leaving ? "splash-leaving" : ""}`}
          data-testid="spiritual-intro"
        >
          <img src={SPLASH_IMG} alt="" className="ken-burns pointer-events-none absolute inset-0 h-full w-full object-cover" />
          <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(90% 72% at 50% 44%, rgba(2,43,37,0.50) 0%, rgba(2,43,37,0.86) 100%)" }} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: GEO, backgroundSize: "150px 150px" }} />
          <div className="si-bloom pointer-events-none absolute left-1/2 top-[9%] h-[520px] w-[520px] -translate-x-1/2 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,154,46,0.20) 0%, transparent 62%)" }} />
          {[...Array(7)].map((_, i) => (
            <span key={i} className="twinkle pointer-events-none absolute rounded-full bg-gold-light" style={{ width: 3, height: 3, left: `${13 + i * 11}%`, top: `${18 + (i % 3) * 17}%`, animationDelay: `${i * 0.75}s` }} />
          ))}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#022B25] via-[#022B25]/55 to-transparent" />

          <div className="relative mx-6 max-w-2xl px-1 text-center text-white">
            <img
              src={LOGO_URL}
              alt="Helping Hands Dubai"
              className="si-reveal mx-auto h-16 w-16 rounded-2xl bg-white/95 object-contain p-1.5 shadow-2xl ring-1 ring-gold/40 sm:h-24 sm:w-24"
              style={{ animationDelay: "0.15s" }}
            />
            <div className="si-reveal mt-5 sm:mt-6" style={{ animationDelay: "0.55s" }}>
              <div className="font-display text-2xl font-bold tracking-tight sm:text-4xl">Helping Hands</div>
              <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold-light sm:text-[11px] sm:tracking-[0.34em]">Dubai · Global Solutions</div>
            </div>

            <div className="si-line mx-auto mt-6 h-px w-40 gold-line sm:mt-7 sm:w-44" style={{ animationDelay: "1.0s" }} />

            <p
              className="si-reveal mt-7 text-[26px] leading-[1.9] sm:mt-8 sm:text-4xl sm:leading-[1.7] lg:text-5xl"
              dir="rtl"
              style={{ fontFamily: "'Amiri', serif", animationDelay: "1.2s" }}
            >
              وَمَنْ أَحْيَاهَا فَكَأَنَّمَا أَحْيَا النَّاسَ جَمِيعًا
            </p>
            <p className="si-reveal mx-auto mt-5 max-w-lg font-display text-base italic leading-relaxed text-emerald-50/90 sm:mt-6 sm:text-xl" style={{ animationDelay: "1.7s" }}>
              "And whoever saves a life — it is as if they had saved all of mankind."
            </p>
            <p className="si-reveal mt-3 text-[10px] uppercase tracking-[0.3em] text-emerald-100/55 sm:text-[11px] sm:tracking-[0.34em]" style={{ animationDelay: "2.0s" }}>Qur'an 5:32</p>

            <div className="si-reveal mt-8 flex flex-wrap items-center justify-center gap-3 sm:mt-10 sm:gap-4" style={{ animationDelay: "2.5s" }}>
              {audioUrl && (
                <button onClick={() => enter(true)} data-testid="enter-with-sound" className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/90 px-8 py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-white transition hover:bg-gold">
                  <Volume2 className="h-4 w-4" /> Enter with Sound
                </button>
              )}
              <button onClick={() => enter(false)} data-testid="enter-intro" className="btn-glow group inline-flex items-center gap-2.5 rounded-full border border-gold/60 bg-white/[0.04] px-9 py-3.5 text-sm font-bold uppercase tracking-[0.2em] text-white backdrop-blur transition hover:border-gold hover:bg-white/[0.09]">
                Enter the Journey <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:rotate-180" />
              </button>
            </div>
          </div>
        </div>
      )}

      {audioUrl && !show && (
        <button onClick={toggleSound} aria-label="Toggle sound" data-testid="sound-toggle" className="fixed bottom-5 start-5 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-900 text-white shadow-lg transition hover:bg-emerald-800">
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      )}
    </>
  );
}
